import { useState } from "preact/hooks"
import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { api_fetch, api_fetch_throws } from "@/api/client.tsx"
import { toast } from "@/components/ui/use-toast"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { RefreshFlag } from "@/refresh_flag.tsx"
import {
  type AgentRun,
  type WizReviewState,
  type WizRunKind,
  can_execute_run,
  executed_action_kinds,
  format_agent_run_deduction_amount,
  run_kind,
  run_review_state,
  sort_runs_for_display,
} from "@/api/agent_run.ts"
import { WizardActionsEditor } from "@/deduction_detail/WizardActionsEditor.tsx"

const ACTION_COLOR: Record<string, string> = {
  file_dispute: "bg-green-100 text-green-800",
  update_status: "bg-blue-100 text-blue-800",
  update_fields: "bg-cyan-100 text-cyan-800",
  request_backup: "bg-yellow-100 text-yellow-800",
  assign_task: "bg-purple-100 text-purple-800",
  add_note: "bg-gray-100 text-gray-700",
  categorize: "bg-teal-100 text-teal-800",
  complete_task: "bg-indigo-100 text-indigo-800",
  match_promotions: "bg-fuchsia-100 text-fuchsia-800",
  skip: "bg-gray-50 text-gray-400",
  error: "bg-red-100 text-red-700",
}

const ACTION_LABEL: Record<string, string> = {
  file_dispute: "Filed dispute",
  update_status: "Updated status",
  update_fields: "Updated fields",
  request_backup: "Requested backup",
  assign_task: "Assigned task",
  add_note: "Added note",
  categorize: "Categorized",
  complete_task: "Completed task",
  match_promotions: "Matched promotions",
  skip: "Skipped",
  error: "Error",
}

/** Wizard confidence (0–1) from the stored decision payload, if present. */
function run_confidence(run: AgentRun): number | null {
  const c = (run.action_payload as { confidence?: unknown } | undefined)?.confidence
  return typeof c === "number" && !Number.isNaN(c) ? c : null
}

const RUN_KIND_CHIP: Record<WizRunKind, { label: string; className: string }> = {
  background: { label: "Scheduled", className: "bg-gray-100 text-gray-600 border-gray-200" },
  bigwiz: { label: "BigWiz", className: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  customwiz: { label: "CustomWiz", className: "bg-violet-100 text-violet-700 border-violet-200" },
}

const REVIEW_STATE_CHIP: Record<WizReviewState, { label: string; className: string }> = {
  pending_review: { label: "Needs review", className: "bg-amber-50 text-amber-700 border-amber-200" },
  executed: { label: "Executed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed: { label: "Failed", className: "bg-red-50 text-red-700 border-red-200" },
  informational: { label: "No action", className: "bg-gray-50 text-gray-500 border-gray-200" },
}

interface WizardRunsSectionProps {
  deduction_id: string
}

export function WizardRunsSection({ deduction_id }: WizardRunsSectionProps) {
  const { data: runs, mutate } = useSWR<AgentRun[]>(
    `/agent/runs?deduction_id=${deduction_id}`,
    () => api_fetch_throws<AgentRun[]>(`/agent/runs?deduction_id=${deduction_id}&limit=10`)
  )

  if (!runs || runs.length === 0) return null
  // Newest run is the headline (it reflects the deduction's current state, and is
  // the one just executed); everything older collapses into history below.
  const [primary_run, ...previous_runs] = sort_runs_for_display(runs)

  async function handle_rerun(run_id: string, feedback: string) {
    const result = await api_fetch(`/agent/runs/${run_id}/rerun`, {
      method: "POST",
      body: { feedback: feedback.trim() || null },
    })
    if (result.ok) {
      toast({ title: "BigWiz re-queued", description: "The wizard will re-analyse this deduction shortly." })
      mutate()
    } else {
      toast({ title: "Failed to re-run wizard", description: result.error?.message ?? "Unknown error", variant: "destructive" })
    }
  }

  return (
    <div class="border-t-6 border-indigo-500 bg-indigo-50 rounded-md p-4">
      <h3 class="text-lg font-semibold leading-5 mb-3">Wizard</h3>
      <div class="space-y-3">
        <WizardRunCard
          key={primary_run.id}
          run={primary_run}
          onRerun={handle_rerun}
          on_mutate={() => void mutate()}
        />

        {previous_runs.length > 0 && (
          <div class="space-y-1.5">
            <p class="text-xs font-medium text-gray-500 pt-1">
              Previous runs ({previous_runs.length})
            </p>
            {previous_runs.map(run => (
              <CollapsedRunRow
                key={run.id}
                run={run}
                onRerun={handle_rerun}
                on_mutate={() => void mutate()}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface WizardRunCardProps {
  run: AgentRun
  onRerun: (run_id: string, feedback: string) => Promise<void>
  on_mutate: () => void
}

/**
 * A historical run: one compact line until the user opens it, at which point it
 * renders the same card as the headline run. Keeps the panel scannable when a
 * deduction has been through the wizard many times, without hiding that an older
 * run still needs review (its state chip stays visible while collapsed).
 */
function CollapsedRunRow({ run, onRerun, on_mutate }: WizardRunCardProps) {
  const [open, setOpen] = useState(false)
  const review_chip = REVIEW_STATE_CHIP[run_review_state(run)]
  const kind_chip = RUN_KIND_CHIP[run_kind(run)]
  const label = ACTION_LABEL[run.action_taken] ?? run.action_taken

  if (open) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          class="text-xs text-gray-500 hover:text-gray-700 mb-1"
          aria-expanded={true}
        >
          ▾ Hide this run
        </button>
        <WizardRunCard run={run} onRerun={onRerun} on_mutate={on_mutate} />
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-expanded={false}
      class="w-full flex items-center gap-2 flex-wrap rounded border border-gray-200 bg-white/70 px-2.5 py-1.5 text-left hover:bg-white"
    >
      <span class="text-gray-400 text-xs">▸</span>
      <span class="text-xs font-medium text-gray-700 truncate max-w-[14rem]">{label}</span>
      <span class={`shrink-0 text-[10px] border rounded px-1 py-0.5 ${kind_chip.className}`}>{kind_chip.label}</span>
      <span class={`shrink-0 text-[10px] border rounded px-1 py-0.5 ${review_chip.className}`}>{review_chip.label}</span>
      <span class="ml-auto text-[11px] text-gray-400 shrink-0">
        {format(parseISO(run.created_at), "MMM d, h:mm a")}
      </span>
    </button>
  )
}

function WizardRunCard({ run, onRerun, on_mutate }: WizardRunCardProps) {
  const [expanded, setExpanded] = useState(false)
  const [rerunOpen, setRerunOpen] = useState(false)
  const [feedback, setFeedback] = useState("")
  const [rerunning, setRerunning] = useState(false)

  const badge = ACTION_COLOR[run.action_taken] ?? "bg-gray-100 text-gray-600"
  const label = ACTION_LABEL[run.action_taken] ?? run.action_taken
  const kind_chip = RUN_KIND_CHIP[run_kind(run)]
  const review_chip = REVIEW_STATE_CHIP[run_review_state(run)]
  const confidence = run_confidence(run)
  const confidence_class =
    confidence == null
      ? ""
      : confidence >= 0.8
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : confidence >= 0.5
          ? "bg-amber-50 text-amber-700 border-amber-200"
          : "bg-red-50 text-red-700 border-red-200"

  const desc_preview =
    run.category_description && run.category_description.length > 100
      ? `${run.category_description.slice(0, 97)}…`
      : run.category_description

  // Reviewing a run is not an admin power — every signed-in member of the org sees the
  // suggested actions and can run them (mirrors the backend's `can_use_wizard`).
  const can_show_dry_payload = run.dry_run
  const can_execute_dry = can_execute_run(run)
  // An executed run shows its action list unprompted so the ticks are visible —
  // otherwise "what did the wizard actually do?" sits behind a second expand.
  const shows_executed_detail = executed_action_kinds(run) !== null

  async function submit_rerun() {
    setRerunning(true)
    try {
      await onRerun(run.id, feedback)
      setRerunOpen(false)
      setFeedback("")
    } finally {
      setRerunning(false)
    }
  }

  const rerun_button = (
    <Button
      size="sm"
      variant="outline"
      class="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
      onClick={() => {
        setRerunOpen(v => !v)
        setFeedback("")
      }}>
      {rerunOpen ? "Hide feedback" : "Provide feedback and re-run"}
    </Button>
  )

  return (
    <div class={`bg-white rounded border p-3 ${run.success ? "border-gray-200" : "border-red-200"}`}>
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 flex-wrap min-w-0">
          <span class={`shrink-0 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${badge}`}>
            {label}
          </span>
          <span class={`shrink-0 text-xs border rounded px-1.5 py-0.5 ${kind_chip.className}`}>{kind_chip.label}</span>
          <span class={`shrink-0 text-xs border rounded px-1.5 py-0.5 ${review_chip.className}`}>
            {review_chip.label}
          </span>
          {confidence != null && (
            <span
              class={`shrink-0 text-xs border rounded px-1.5 py-0.5 ${confidence_class}`}
              title="Wizard confidence in this decision"
            >
              {Math.round(confidence * 100)}% confident
            </span>
          )}
          <span class="text-xs text-gray-400 shrink-0">
            {format(parseISO(run.created_at), "MMM d, h:mm a")}
          </span>
        </div>
      </div>

      {run.custom_prompt && (
        <p class="mt-2 text-xs text-violet-800 italic line-clamp-2" title={run.custom_prompt}>
          Prompt: “{run.custom_prompt}”
        </p>
      )}

      {run.error && (
        <p class="mt-2 text-xs text-red-600 font-mono">{run.error}</p>
      )}

      <div class="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
        <span class="font-medium text-gray-800">{format_agent_run_deduction_amount(run.deduction_amount_usd)}</span>
        <span class="text-gray-300">·</span>
        <span class="truncate max-w-[12rem]" title={run.category_name}>
          {run.category_name}
        </span>
        {desc_preview && (
          <>
            <span class="text-gray-300">·</span>
            <span class="text-gray-500 truncate max-w-[16rem]" title={run.category_description ?? undefined}>
              {desc_preview}
            </span>
          </>
        )}
        <span class="text-gray-300">·</span>
        <span class="shrink-0 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
          {run.status_value.replaceAll("_", " ")}
        </span>
      </div>

      {run.reasoning && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          aria-expanded={expanded}
          class={`mt-2 w-full text-left text-sm text-gray-700 cursor-pointer hover:text-gray-900 ${expanded ? "" : "line-clamp-2"}`}
        >
          {run.reasoning}
        </button>
      )}

      {run.reasoning && run.reasoning.length > 120 && (
        <button
          onClick={() => setExpanded(v => !v)}
          class="text-xs text-gray-500 mt-1 hover:text-gray-700"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}

      {/* The actionable run shows its editable actions inline; historical/read-only
          runs tuck them behind the reasoning expand to avoid clutter. */}
      {(can_execute_dry || shows_executed_detail || (expanded && can_show_dry_payload)) && (
        <div class="mt-3">
          <p class="font-medium text-gray-700 mb-1.5 text-xs">
            {can_execute_dry ? "Review & run suggested actions" : shows_executed_detail ? "Actions" : "Suggested actions"}
          </p>
          <WizardActionsEditor
            run={run}
            readOnly={!can_execute_dry}
            onExecuted={() => {
              on_mutate()
              // Refresh deduction-detail-driven UI (tasks, notes, status, dispute).
              RefreshFlag.set(f => !f)
            }}
            trailing={rerun_button}
          />
        </div>
      )}

      {/* When the actionable editor (with its button row) isn't shown, surface the
          re-run control here so the user can still provide feedback and re-run. */}
      {!can_execute_dry && <div class="mt-3">{rerun_button}</div>}

      {rerunOpen && (
        <div class="mt-3 space-y-2">
          <Textarea
            placeholder="Optional: explain what the wizard got wrong or what to reconsider…"
            value={feedback}
            onInput={(e: Event) => setFeedback((e.target as HTMLTextAreaElement).value)}
            class="text-sm"
            rows={3}
          />
          <div class="flex gap-2">
            <Button size="sm" onClick={submit_rerun} disabled={rerunning}>
              {rerunning ? "Queuing…" : "Re-run Wizard"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setRerunOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
